import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	Badge,
	Box,
	Button,
	Group,
	Loader,
	MediaQuery,
	Paper,
	Stack,
	Table,
	Text,
	Title,
} from '@mantine/core'
import { getCheckInMessageHistory } from '../services/actions/checkInActions'

const STATUS_COLORS = {
	QUEUED: 'gray',
	SENT: 'blue',
	DELIVERED: 'green',
	OPENED: 'grape',
	CLICKED: 'yellow',
	BOUNCED: 'orange',
	FAILED: 'red',
	UNSUBSCRIBED: 'pink',
}

const SENT_STATUSES = new Set([
	'SENT',
	'DELIVERED',
	'OPENED',
	'CLICKED',
	'BOUNCED',
	'UNSUBSCRIBED',
])

const formatDateTime = (value) => {
	if (!value) return 'Not sent'
	const date = new Date(value)
	if (!Number.isFinite(date.getTime())) return 'Not sent'
	return date.toLocaleString([], {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	})
}

const safeText = (value) =>
	value === null || value === undefined ? '' : String(value)

const truncate = (value, maxLength = 160) => {
	const text = safeText(value).trim()
	if (text.length <= maxLength) return text
	return `${text.slice(0, maxLength - 3)}...`
}

const getStatusColor = (status) => STATUS_COLORS[status] || 'gray'

const getMessageTitle = (message) => {
	if (message.subject) return message.subject
	if (message.templateLabel) return message.templateLabel
	return message.sourceLabel || 'Message'
}

const getMessagePreview = (message) => {
	if (message.bodyText) return truncate(message.bodyText)
	return message.bodyUnavailableReason || 'No message body stored.'
}

const getRecipientLabel = (message) => {
	if (message.address) return message.address
	return message.channel === 'SMS' || message.channel === 'MMS'
		? 'No phone recorded'
		: 'No email recorded'
}

const MessageRow = ({ message, orgSlug, experienceSlug }) => (
	<tr>
		<td>{formatDateTime(message.timelineAt)}</td>
		<td>
			<Stack spacing={2}>
				<Text size='sm' fw={600}>
					{message.sourceLabel}
				</Text>
				{message.templateLabel && (
					<Text size='xs' c='dimmed'>
						{message.templateLabel}
					</Text>
				)}
			</Stack>
		</td>
		<td>
			<Text size='sm'>
				{message.channel || 'UNKNOWN'}
			</Text>
		</td>
		<td>{getRecipientLabel(message)}</td>
		<td>
			<Badge color={getStatusColor(message.status)}>{message.status}</Badge>
		</td>
		<td>
			<Stack spacing={2}>
				<Text size='sm' fw={500}>
					{getMessageTitle(message)}
				</Text>
				<Text size='xs' c='dimmed' style={{ whiteSpace: 'pre-wrap' }}>
					{getMessagePreview(message)}
				</Text>
				{message.mediaUrl && (
					<Box mt={4}>
						<img
							src={message.mediaUrl}
							alt='MMS'
							style={{
								width: 120,
								maxHeight: 90,
								objectFit: 'cover',
								borderRadius: 6,
								display: 'block',
							}}
						/>
					</Box>
				)}
				{message.errorMessage && (
					<Text size='xs' c='red'>
						{message.errorMessage}
					</Text>
				)}
			</Stack>
		</td>
		<td>
			{message.blastId ? (
				<Button
					component={Link}
					to={`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${message.blastId}`}
					size='xs'
					variant='light'
				>
					View Blast
				</Button>
			) : (
				<Text size='xs' c='dimmed'>
					Recorded event
				</Text>
			)}
		</td>
	</tr>
)

const MessageCard = ({ message, orgSlug, experienceSlug }) => (
	<Paper withBorder p='sm'>
		<Stack spacing='xs'>
			<Group position='apart' spacing='xs'>
				<Text size='sm' fw={600}>
					{message.channel || 'UNKNOWN'}
				</Text>
				<Badge color={getStatusColor(message.status)}>{message.status}</Badge>
			</Group>
			<Box>
				<Text size='sm' fw={600}>
					{getMessageTitle(message)}
				</Text>
				<Text size='xs' c='dimmed'>
					{formatDateTime(message.timelineAt)}
				</Text>
			</Box>
			<Text size='sm'>{message.sourceLabel}</Text>
			{message.templateLabel && (
				<Text size='xs' c='dimmed'>
					{message.templateLabel}
				</Text>
			)}
			<Text size='xs'>To: {getRecipientLabel(message)}</Text>
			<Text size='xs' c='dimmed' style={{ whiteSpace: 'pre-wrap' }}>
				{getMessagePreview(message)}
			</Text>
			{message.mediaUrl && (
				<img
					src={message.mediaUrl}
					alt='MMS'
					style={{
						width: '100%',
						maxWidth: 240,
						maxHeight: 180,
						objectFit: 'cover',
						borderRadius: 6,
						display: 'block',
					}}
				/>
			)}
			{message.errorMessage && (
				<Text size='xs' c='red'>
					{message.errorMessage}
				</Text>
			)}
			{message.blastId && (
				<Button
					component={Link}
					to={`/${orgSlug}/experiences/${experienceSlug}/messaging/blasts/${message.blastId}`}
					size='xs'
					variant='light'
					style={{ alignSelf: 'flex-start' }}
				>
					View Blast
				</Button>
			)}
		</Stack>
	</Paper>
)

const CheckInMessageHistoryPage = () => {
	const { orgSlug, experienceSlug, id } = useParams()
	const navigate = useNavigate()
	const location = useLocation()
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [history, setHistory] = useState({ checkIn: null, messages: [] })

	const loadHistory = useCallback(async () => {
		setLoading(true)
		try {
			const data = await getCheckInMessageHistory(orgSlug, id)
			setHistory({
				checkIn: data?.checkIn || null,
				messages: Array.isArray(data?.messages) ? data.messages : [],
			})
			setError('')
		} catch (err) {
			console.error('Failed to load check-in message history:', err)
			setError('Failed to load message history.')
		} finally {
			setLoading(false)
		}
	}, [orgSlug, id])

	useEffect(() => {
		loadHistory()
	}, [loadHistory])

	const checkIn = history.checkIn
	const messages = history.messages
	const sentCount = useMemo(
		() =>
			messages.filter((message) => SENT_STATUSES.has(message.status)).length,
		[messages],
	)
	const emailCount = useMemo(
		() => messages.filter((message) => message.channel === 'EMAIL').length,
		[messages],
	)
	const smsCount = useMemo(
		() => messages.filter((message) => message.channel === 'SMS').length,
		[messages],
	)
	const mmsCount = useMemo(
		() => messages.filter((message) => message.channel === 'MMS').length,
		[messages],
	)

	const checkInPath = `/${orgSlug}/experiences/${experienceSlug}/checkIn/${id}`
	const returnTo =
		typeof location.state?.returnTo === 'string' &&
		location.state.returnTo.trim().startsWith('/')
			? location.state.returnTo
			: checkInPath

	return (
		<Box px='md' py='md'>
			<Button
				variant='subtle'
				size='xs'
				mb='sm'
				onClick={() => navigate(returnTo)}
			>
				Back
			</Button>

			<Group position='apart' align='flex-start' mb='md'>
				<Box>
					<Title order={2}>Message History</Title>
					<Text c='dimmed'>
						{checkIn?.travelerName || 'Traveler'}
						{checkIn?.bookingNumber
							? ` | Booking ${checkIn.bookingNumber}`
							: ''}
					</Text>
					{checkIn?.experienceName && (
						<Text size='sm' c='dimmed'>
							{checkIn.experienceName}
						</Text>
					)}
				</Box>
				<Button variant='default' onClick={loadHistory} disabled={loading}>
					Refresh
				</Button>
			</Group>

			{error && (
				<Text c='red' mb='md'>
					{error}
				</Text>
			)}

			{loading ? (
				<Loader />
			) : (
				<Stack spacing='md'>
					<Group spacing='xs'>
						<Badge color='gray'>total {messages.length}</Badge>
						<Badge color='blue'>sent {sentCount}</Badge>
						<Badge color='indigo'>email {emailCount}</Badge>
						<Badge color='teal'>sms {smsCount}</Badge>
						<Badge color='cyan'>mms {mmsCount}</Badge>
					</Group>

					{messages.length === 0 ? (
						<Paper withBorder p='md'>
							<Text c='dimmed'>No messages found for this check-in.</Text>
						</Paper>
					) : (
						<>
							<MediaQuery largerThan='sm' styles={{ display: 'none' }}>
								<Stack spacing='sm'>
									{messages.map((message) => (
										<MessageCard
											key={message.id}
											message={message}
											orgSlug={orgSlug}
											experienceSlug={experienceSlug}
										/>
									))}
								</Stack>
							</MediaQuery>

							<MediaQuery smallerThan='sm' styles={{ display: 'none' }}>
								<Paper withBorder p='sm'>
									<Table striped highlightOnHover verticalSpacing='sm'>
										<thead>
											<tr>
												<th>Sent</th>
												<th>Source</th>
												<th>Channel</th>
												<th>Recipient</th>
												<th>Status</th>
												<th>Message</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>
											{messages.map((message) => (
												<MessageRow
													key={message.id}
													message={message}
													orgSlug={orgSlug}
													experienceSlug={experienceSlug}
												/>
											))}
										</tbody>
									</Table>
								</Paper>
							</MediaQuery>
						</>
					)}
				</Stack>
			)}
		</Box>
	)
}

export default CheckInMessageHistoryPage
